import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { portalCommands } from 'src/app/utils/portal-path';
import { MajorsService } from 'src/app/services/majors.service';
import { ClassesService } from 'src/app/services/classes.service';
import { ClassItem, Major } from '../majors.models';

@Component({
  selector: 'app-major-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.css']
})
export class MajorDetailComponent {
  major: Major | null = null;
  majorClasses: ClassItem[] = [];
  loading = true;
  loadingClasses = false;
  errorMessage = '';
  detailError = '';
  open = false;
  classForm = new FormGroup({
    name: new FormControl('', [Validators.required])
  });
  editForm = new FormGroup({
    name: new FormControl('', [Validators.required])
  });
  addingClass = false;
  editError = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private majorsService: MajorsService,
    private classesService: ClassesService
  ) {}

  async ngOnInit() {
    this.route.paramMap.subscribe(async params => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(portalCommands(this.router, 'settings', 'majors'));
        return;
      }
      await this.loadMajor(id);
    });
  }

  async loadMajor(id: string) {
    this.loading = true;
    this.errorMessage = '';
    try {
      this.major = await this.majorsService.getById(id);
      if (!this.major) {
        this.errorMessage = 'MAJORS.NOT_FOUND';
        return;
      }
      await this.loadClasses(id);
    } catch {
      this.errorMessage = 'MAJORS.LOAD_FAILED';
    } finally {
      this.loading = false;
    }
  }

  async loadClasses(majorId: string) {
    this.loadingClasses = true;
    this.detailError = '';
    try {
      this.majorClasses = await this.classesService.listByMajor(majorId);
    } catch (error: any) {
      this.detailError = error?.error?.errorMessages?.[0]?.message || 'MAJORS.CLASSES_LOAD_FAILED';
      this.majorClasses = [];
    } finally {
      this.loadingClasses = false;
    }
  }

  get majorsLink(): string[] {
    return portalCommands(this.router, 'settings', 'majors');
  }

  goBack() {
    this.router.navigate(portalCommands(this.router, 'settings', 'majors'));
  }

  onEdit() {
    if (!this.major) {
      return;
    }
    this.editForm.patchValue({ name: this.major.name });
    this.editError = '';
    this.open = true;
  }

  closeEditModal() {
    this.open = false;
    this.editError = '';
  }

  async submitEdit() {
    if (!this.major || this.editForm.invalid) {
      this.editError = 'Name is required';
      return;
    }

    try {
      const name = this.editForm.value.name as string;
      await this.majorsService.update(this.major.id, { name });
      this.major = { ...this.major, name };
      this.open = false;
    } catch (error: any) {
      this.editError = error?.error?.errorMessages?.[0]?.message || 'MAJORS.SAVE_FAILED';
    }
  }

  async addClassToMajor() {
    if (!this.major || this.classForm.invalid) {
      this.detailError = 'MAJORS.CLASS_NAME_REQUIRED';
      return;
    }

    this.addingClass = true;
    this.detailError = '';
    try {
      await this.classesService.create({
        name: this.classForm.value.name as string,
        major: this.major.id
      });
      this.classForm.reset();
      await this.loadClasses(this.major.id);
    } catch (error: any) {
      this.detailError = error?.error?.errorMessages?.[0]?.message || 'MAJORS.CLASS_ADD_FAILED';
    } finally {
      this.addingClass = false;
    }
  }

  async deleteClass(classItem: ClassItem) {
    if (!this.major) {
      return;
    }

    this.detailError = '';
    try {
      await this.classesService.delete(classItem.id);
      await this.loadClasses(this.major.id);
    } catch (error: any) {
      this.detailError = error?.error?.errorMessages?.[0]?.message || 'MAJORS.CLASS_DELETE_FAILED';
    }
  }
}
